import type { Item } from '@/stores/canvasStore';

const GRID_SNAP = 20;       // Canvas grid size
const GAP = 40;              // Gap between source items and result item
const OVERLAP_PADDING = 20;  // Extra padding when avoiding overlaps

/** Snap a value to the nearest grid point */
function snapToGrid(value: number): number {
  return Math.round(value / GRID_SNAP) * GRID_SNAP;
}

/** Get bounding box of a set of items */
function getBoundingBox(items: Item[]): { 
  minX: number; minY: number; maxX: number; maxY: number; 
  centerX: number; centerY: number; width: number; height: number;
} {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const item of items) {
    minX = Math.min(minX, item.position_x);
    minY = Math.min(minY, item.position_y);
    maxX = Math.max(maxX, item.position_x + item.width);
    maxY = Math.max(maxY, item.position_y + item.height);
  }
  return {
    minX, minY, maxX, maxY,
    centerX: (minX + maxX) / 2,
    centerY: (minY + maxY) / 2,
    width: maxX - minX,
    height: maxY - minY,
  };
}

/** Check if a rect overlaps any existing items */
function hasOverlap(
  x: number, y: number, w: number, h: number, 
  allItems: Item[], excludeIds: Set<string>
): boolean {
  for (const item of allItems) {
    if (excludeIds.has(item.id)) continue;
    const overlapX = x < item.position_x + item.width + OVERLAP_PADDING && 
                     x + w + OVERLAP_PADDING > item.position_x;
    const overlapY = y < item.position_y + item.height + OVERLAP_PADDING && 
                     y + h + OVERLAP_PADDING > item.position_y;
    if (overlapX && overlapY) return true;
  }
  return false;
}

/**
 * Calculate the best position for a new AI-generated item
 * relative to the source items that were used as context.
 * 
 * Strategy:
 * 1. Single item: try right, then below, then left
 * 2. Multiple items: try below bounding box (centered), then right, then left
 * 3. Fall back to below-right if all positions overlap
 */
export function calculatePlacement(
  sourceItems: Item[],
  resultWidth: number,
  resultHeight: number,
  allCanvasItems: Item[],
): { x: number; y: number } {
  const bbox = getBoundingBox(sourceItems);
  const sourceIds = new Set(sourceItems.map(i => i.id));

  // Candidate positions in priority order
  const candidates: { x: number; y: number }[] = [];

  if (sourceItems.length === 1) {
    const item = sourceItems[0];
    // Right of single item (vertically centered)
    candidates.push({
      x: item.position_x + item.width + GAP,
      y: item.position_y + (item.height / 2) - (resultHeight / 2),
    });
    // Below single item (left-aligned)
    candidates.push({
      x: item.position_x,
      y: item.position_y + item.height + GAP,
    });
    // Left of single item (vertically centered)
    candidates.push({
      x: item.position_x - resultWidth - GAP,
      y: item.position_y + (item.height / 2) - (resultHeight / 2),
    });
  } else {
    // Below bounding box, centered
    candidates.push({
      x: bbox.centerX - (resultWidth / 2),
      y: bbox.maxY + GAP,
    });
    // Right of bounding box, vertically centered
    candidates.push({
      x: bbox.maxX + GAP,
      y: bbox.centerY - (resultHeight / 2),
    });
    // Left of bounding box, vertically centered
    candidates.push({
      x: bbox.minX - resultWidth - GAP,
      y: bbox.centerY - (resultHeight / 2),
    });
    // Below-right corner
    candidates.push({
      x: bbox.maxX + GAP,
      y: bbox.maxY + GAP,
    });
  }

  // Find first non-overlapping candidate
  for (const candidate of candidates) {
    const snappedX = snapToGrid(candidate.x);
    const snappedY = snapToGrid(Math.max(0, candidate.y)); // Don't go above canvas origin
    
    if (!hasOverlap(snappedX, snappedY, resultWidth, resultHeight, allCanvasItems, sourceIds)) {
      return { x: snappedX, y: snappedY };
    }
  }

  // Fallback: place below bounding box with offset to avoid clusters
  return {
    x: snapToGrid(bbox.centerX - (resultWidth / 2)),
    y: snapToGrid(bbox.maxY + GAP * 2),
  };
}
